import { Button } from "@povio/ui";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { produce } from "immer";
import { useSearchParams } from "next/navigation";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";

import { AddIcon } from "@/assets/icons/ui/Add";
import EndorsementsItem from "@/components/endorsements/EndorsementsItem";
import RequestEndorsementModal from "@/components/endorsements/RequestEndorsementModal";
import type { ISectionProps } from "@/components/home/sections/ProfileBioSection";
import ProfileSectionCard from "@/components/home/sections/ProfileSectionCard";
import { SkeletonWrapper } from "@/components/onboarding/ui/Skeleton";
import { DemoClickWrapper } from "@/components/shared/demo/DemoClickWrapper";
import { Typography } from "@/components/ui/text/Typography/Typography";
import type { CommonModels } from "@/data/common/common.models";
import { EndorsementsQueries } from "@/data/endorsements/endorsements.queries";
import { PublicProfileQueries } from "@/data/publicProfile/publicProfile.queries";
import { useDemoAlternativeQuery } from "@/hooks/demo/useDemoAlternativeQuery";
import { useToast } from "@/hooks/useToast";
import { DemoContext } from "@/providers/demo/DemoContext";
import { useEndorsementModalsStore } from "@/providers/endorsement/endorsement-modals.store";

interface IProps extends ISectionProps {}

export enum EndorsementTab {
  INCOMING = "incoming",
  OUTGOING = "outgoing",
}

const ProfileEndorsementsSection = ({ onEditSectionClicked, isPublic = false, enableInViewAnimation }: IProps) => {
  const queryClient = useQueryClient();
  const searchParams = useSearchParams();
  const urlTab = searchParams.get("tab");
  const sectionRef = useRef<HTMLDivElement>(null);
  const { isDemoMode, demoUserId } = DemoContext.useDemo();

  // In demo mode, force INCOMING tab (no outgoing endorsements)
  const [activeTab, setActiveTab] = useState<EndorsementTab>(
    isDemoMode ? EndorsementTab.INCOMING : (urlTab as EndorsementTab) || EndorsementTab.INCOMING,
  );
  const [showHidden, setShowHidden] = useState(false);
  const [showRequestEndorsementModal, setShowRequestEndorsementModal] = useState(false);
  const { successToast, errorToast } = useToast();
  const { setEndorsement } = useEndorsementModalsStore();

  const { mutate: approveEndorsement } = EndorsementsQueries.useApprove({
    onSuccess: () => {
      successToast({ text: "Endorsement approved" });

      queryClient.invalidateQueries({ queryKey: EndorsementsQueries.keys.getIncoming(50) });
      queryClient.invalidateQueries({ queryKey: EndorsementsQueries.keys.getOutgoing(50) });
    },
    onError: () => {
      errorToast({ text: "Failed to approve endorsement" });
    },
  });

  const { mutate: deleteEndorsement } = EndorsementsQueries.useDeleteEndorsement({
    onMutate: async (variables) => {
      const deletedId = variables?.id;

      if (deletedId === undefined) {
        return;
      }

      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: EndorsementsQueries.keys.getIncoming(50) });
      await queryClient.cancelQueries({ queryKey: EndorsementsQueries.keys.getOutgoing(50) });

      // Snapshot the previous values for rollback
      const previousIncomingData = queryClient.getQueryData(EndorsementsQueries.keys.getIncoming(50)) as any;
      const previousOutgoingData = queryClient.getQueryData(EndorsementsQueries.keys.getOutgoing(50)) as any;

      // Optimistically remove the endorsement from cache
      queryClient.setQueryData(EndorsementsQueries.keys.getOutgoing(50), (oldData: any) => {
        if (!oldData) return oldData;
        return produce(oldData, (draft: any) => {
          const index = draft?.items?.findIndex((e: any) => e.id === deletedId);
          if (index !== -1) {
            draft?.items?.splice(index, 1);
          }
        });
      });

      queryClient.setQueryData(EndorsementsQueries.keys.getIncoming(50), (oldData: any) => {
        if (!oldData) return oldData;
        return produce(oldData, (draft: any) => {
          const index = draft?.items?.findIndex((e: any) => e.id === deletedId);
          if (index !== -1) {
            draft?.items?.splice(index, 1);
          }
        });
      });

      // eslint-disable-next-line consistent-return
      return { previousIncomingData, previousOutgoingData };
    },
    onError: (err, variables, context: any) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousIncomingData) {
        queryClient.setQueryData(EndorsementsQueries.keys.getIncoming(50), context.previousIncomingData);
      }
      if (context?.previousOutgoingData) {
        queryClient.setQueryData(EndorsementsQueries.keys.getOutgoing(50), context.previousOutgoingData);
      }
      errorToast({ text: "Failed to delete endorsement" });
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: EndorsementsQueries.keys.getIncoming(50) });
      queryClient.invalidateQueries({ queryKey: EndorsementsQueries.keys.getOutgoing(50) });
    },
  });

  const { mutate: updateVisibility } = EndorsementsQueries.useUpdateVisibility({
    onMutate: async (variables) => {
      const updatedId = variables?.id;
      const newVisibility = variables?.data?.visible;

      if (updatedId === undefined || newVisibility === undefined) {
        return;
      }

      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: EndorsementsQueries.keys.getIncoming(50) });
      await queryClient.cancelQueries({ queryKey: EndorsementsQueries.keys.getOutgoing(50) });

      // Snapshot the previous values for rollback
      const previousIncomingData = queryClient.getQueryData(EndorsementsQueries.keys.getIncoming(50)) as any;
      const previousOutgoingData = queryClient.getQueryData(EndorsementsQueries.keys.getOutgoing(50)) as any;

      // Optimistically update the cache
      queryClient.setQueryData(EndorsementsQueries.keys.getIncoming(50), (oldData: any) => {
        if (!oldData) return oldData;
        return produce(oldData, (draft: any) => {
          const endorsement = draft?.items?.find((e: any) => e.id === updatedId);
          if (endorsement) {
            endorsement.visible = newVisibility;
          }
        });
      });

      queryClient.setQueryData(EndorsementsQueries.keys.getOutgoing(50), (oldData: any) => {
        if (!oldData) return oldData;
        return produce(oldData, (draft: any) => {
          const endorsement = draft?.items?.find((e: any) => e.id === updatedId);
          if (endorsement) {
            endorsement.visible = newVisibility;
          }
        });
      });

      // eslint-disable-next-line consistent-return
      return { previousIncomingData, previousOutgoingData };
    },
    onError: (err, variables, context: any) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousIncomingData) {
        queryClient.setQueryData(EndorsementsQueries.keys.getIncoming(50), context.previousIncomingData);
      }
      if (context?.previousOutgoingData) {
        queryClient.setQueryData(EndorsementsQueries.keys.getOutgoing(50), context.previousOutgoingData);
      }
      errorToast({ text: "Failed to update endorsement visibility" });
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: EndorsementsQueries.keys.getIncoming(50) });
      queryClient.invalidateQueries({ queryKey: EndorsementsQueries.keys.getOutgoing(50) });
      successToast({ text: "Endorsement visibility updated successfully" });
    },
  });

  const { data: incomingEndorsements, isLoading: isLoadingIncomingEndorsements } = useDemoAlternativeQuery<any>(
    {
      query: EndorsementsQueries.useGetIncoming,
      params: [{ limit: 50 }],
      options: { enabled: activeTab === EndorsementTab.INCOMING, staleTime: 0 },
    },
    {
      query: PublicProfileQueries.useGetEndorsements as any,
      params: [{ userId: demoUserId }],
      options: {
        enabled: activeTab === EndorsementTab.INCOMING,
        staleTime: 0,
        // Transform flat array to match paginated response format
        select: (data: any) => ({
          items: data || [],
          limit: 50,
          totalItems: data?.length || 0,
        }),
      },
    },
  );

  const { data: outgoingEndorsements, isLoading: isLoadingOutgoingEndorsements } = EndorsementsQueries.useGetOutgoing(
    { limit: 50 },
    { enabled: !isDemoMode && activeTab === EndorsementTab.OUTGOING, staleTime: 0 },
  );

  const endorsements = useMemo(() => {
    if (activeTab === EndorsementTab.INCOMING) {
      return (incomingEndorsements?.items || []) as CommonModels.EndorsementDto[];
    }
    if (activeTab === EndorsementTab.OUTGOING) {
      return (outgoingEndorsements?.items || []) as CommonModels.EndorsementDto[];
    }

    return [];
  }, [activeTab, incomingEndorsements, outgoingEndorsements]);

  const isLoading = isLoadingIncomingEndorsements || isLoadingOutgoingEndorsements;

  const onAddNewEndorsement = () => {
    setShowRequestEndorsementModal(true);
  };

  const onShowMore = () => {
    setShowHidden(!showHidden);
  };

  const onSetTab = (tab: EndorsementTab) => {
    setActiveTab(tab);
    setShowHidden(false);
  };

  const onWriteEndorsement = (endorsement: CommonModels.EndorsementDto) => {
    setEndorsement({
      type: "fill",
      endorsement,
    });
  };

  const splitEndorsements = useMemo(() => {
    if (activeTab === EndorsementTab.INCOMING) {
      return {
        visible: endorsements?.filter((endorsement) => endorsement.visible) || [],
        hidden: endorsements?.filter((endorsement) => !endorsement.visible) || [],
      };
    }
    return {
      visible: endorsements,
      hidden: [],
    };
  }, [activeTab, endorsements]);

  const onAcceptEndorsement = (endorsement: CommonModels.EndorsementDto) => {
    approveEndorsement({
      id: endorsement.id,
    });
  };
  const onToggleEndorsementVisibility = (endorsement: CommonModels.EndorsementDto) => {
    updateVisibility({ id: endorsement.id, data: { visible: !endorsement.visible } });
  };
  const onDeleteEndorsement = (endorsement: CommonModels.EndorsementDto) => {
    deleteEndorsement(
      { id: endorsement.id },
      {
        onSuccess: () => {
          successToast({ text: "Endorsement deleted" });
        },
        onError: () => {
          errorToast({ text: "Failed to delete endorsement" });
        },
      },
    );
  };

  const onIgnoreEndorsement = (endorsement: CommonModels.EndorsementDto) => {
    deleteEndorsement(
      { id: endorsement.id },
      {
        onSuccess: () => {
          successToast({ text: "Endorsement ignored" });

          queryClient.invalidateQueries({ queryKey: EndorsementsQueries.keys.getIncoming(50) });
          queryClient.invalidateQueries({ queryKey: EndorsementsQueries.keys.getOutgoing(50) });
        },
        onError: () => {
          errorToast({ text: "Failed to ignore endorsement" });
        },
      },
    );
  };

  const onCancelEndorsement = (endorsement: CommonModels.EndorsementDto) => {
    deleteEndorsement(
      { id: endorsement.id },
      {
        onSuccess: () => {
          successToast({ text: "Endorsement cancelled" });
        },
      },
    );
  };
  const onCancelEndorsementRequest = (endorsement: CommonModels.EndorsementDto) => {
    deleteEndorsement(
      { id: endorsement.id },
      {
        onSuccess: () => {
          successToast({ text: "Endorsement request cancelled" });
        },
      },
    );
  };
  const onUnsendEndorsement = (endorsement: CommonModels.EndorsementDto) => {
    deleteEndorsement(
      { id: endorsement.id },
      {
        onSuccess: () => {
          successToast({ text: "Endorsement canceled" });
        },
      },
    );
  };

  useEffect(() => {
    if (urlTab && sectionRef.current) {
      const scrollContainer = document.getElementById("scroll-container");
      if (scrollContainer) {
        const sectionElement = sectionRef.current;
        const containerRect = scrollContainer.getBoundingClientRect();
        const sectionRect = sectionElement.getBoundingClientRect();

        const scrollTop = scrollContainer.scrollTop + (sectionRect.top - containerRect.top) - 50;

        scrollContainer.scrollTo({
          top: scrollTop,
          behavior: "smooth",
        });
      }
    }
  }, [urlTab]);

  return (
    <ProfileSectionCard
      title="Endorsements"
      titleSize="title-3"
      titleActions={
        <div className="flex items-center gap-2">
          <Button
            variant="outlined"
            color="secondary"
            size="xs"
            onPress={() => onSetTab(EndorsementTab.INCOMING)}
            className={clsx(
              { "bg-interactive-outlined-secondary-outline-idle!": activeTab === EndorsementTab.INCOMING },
              "px-4! py-1!",
            )}
          >
            Incoming
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            size="xs"
            onPress={() => onSetTab(EndorsementTab.OUTGOING)}
            className={clsx(
              { "bg-interactive-outlined-secondary-outline-idle!": activeTab === EndorsementTab.OUTGOING },
              "px-4! py-1!",
            )}
          >
            Outgoing
          </Button>
        </div>
      }
      onEditSectionClicked={onEditSectionClicked}
      isPublic={isPublic}
      enableInViewAnimation={enableInViewAnimation}
    >
      <div
        className="flex flex-col gap-8"
        ref={sectionRef}
      >
        <div className="flex flex-col gap-5">
          {isLoading && (
            <div className="flex flex-col gap-1">
              <SkeletonWrapper className="h-[80px]" />
              <SkeletonWrapper className="h-[80px]" />
            </div>
          )}
          {splitEndorsements?.visible?.map((endorsement, index) => (
            <Fragment key={endorsement.id}>
              <EndorsementsItem
                endorsement={endorsement}
                activeTab={activeTab}
                onAcceptEndorsement={onAcceptEndorsement}
                onToggleEndorsementVisibility={onToggleEndorsementVisibility}
                onDeleteEndorsement={onDeleteEndorsement}
                onWriteEndorsement={onWriteEndorsement}
                onCancelEndorsement={onCancelEndorsement}
                onCancelEndorsementRequest={onCancelEndorsementRequest}
                onUnsendEndorsement={onUnsendEndorsement}
                onIgnoreEndorsement={onIgnoreEndorsement}
              />
              {index !== splitEndorsements.visible.length - 1 && <div className="h-[1px] w-full bg-black" />}
            </Fragment>
          ))}

          {!isLoading && !splitEndorsements.visible?.length && (
            <Typography
              variant="prominent-1"
              size="labels-xs"
              className="ml-8 t:ml-0 text-secondary-100"
              data-testid="no-projects-text"
            >
              There are currently no visible {activeTab} endorsements
            </Typography>
          )}
          {showHidden && !!splitEndorsements.hidden?.length && (
            <>
              <div className="h-[1px] w-full bg-black" />
              <Button
                variant="outlined"
                size="xs"
                className="border-none text-blue-200!"
                width="hug"
                onPress={onShowMore}
                data-testid="show-more-endorsements-button"
              >
                Hide
              </Button>
              {splitEndorsements.hidden?.map((endorsement, index) => (
                <Fragment key={endorsement.id}>
                  <EndorsementsItem
                    endorsement={endorsement}
                    activeTab={activeTab}
                    onAcceptEndorsement={onAcceptEndorsement}
                    onToggleEndorsementVisibility={onToggleEndorsementVisibility}
                    onDeleteEndorsement={onDeleteEndorsement}
                    onWriteEndorsement={onWriteEndorsement}
                    onCancelEndorsement={onCancelEndorsement}
                    onCancelEndorsementRequest={onCancelEndorsementRequest}
                    onUnsendEndorsement={onUnsendEndorsement}
                    onIgnoreEndorsement={onIgnoreEndorsement}
                  />
                  {index !== splitEndorsements.hidden.length - 1 && <div className="h-[1px] w-full bg-black" />}
                </Fragment>
              ))}
            </>
          )}
        </div>
        <div className="flex w-full flex-row flex-wrap items-center justify-start dd:justify-between">
          {!!splitEndorsements.hidden?.length && !showHidden && (
            <Button
              variant="outlined"
              size="xs"
              className="border-none text-blue-200!"
              width="hug"
              onPress={onShowMore}
              data-testid="show-more-endorsements-button"
            >
              Show Hidden
            </Button>
          )}
          <DemoClickWrapper>
            <Button
              icon={AddIcon}
              variant="outlined"
              size="xs"
              className="border-none text-neutral-100!"
              iconClassName="h-3 w-3"
              iconPosition="left"
              width="hug"
              onPress={onAddNewEndorsement}
              data-testid="add-new-endorsement-button"
            >
              Request endorsement
            </Button>
          </DemoClickWrapper>
        </div>
      </div>

      <RequestEndorsementModal
        isOpen={showRequestEndorsementModal}
        onClose={() => setShowRequestEndorsementModal(false)}
      />
    </ProfileSectionCard>
  );
};

export default ProfileEndorsementsSection;
